import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * Camera-based barcode scanner component.
 * Uses html5-qrcode to access the device camera and scan barcodes.
 * 
 * Props:
 *   onScan(barcode: string) — called when a barcode is detected
 *   onClose() — called when user closes the scanner
 */
export default function BarcodeScanner({ onScan, onClose }) {
    const scannerRef = useRef(null);
    const html5QrRef = useRef(null);
    const [error, setError] = useState(null);
    const [scanning, setScanning] = useState(false);

    useEffect(() => {
        const scanner = new Html5Qrcode('barcode-reader');
        html5QrRef.current = scanner;

        const config = {
            fps: 10,
            qrbox: { width: 280, height: 160 },
            aspectRatio: 1.0,
            formatsToSupport: [
                0,  // QR_CODE
                4,  // CODE_128
                2,  // CODE_39
                11, // EAN_13
                12, // EAN_8
                13, // UPC_A
                14, // UPC_E
            ],
        };

        scanner
            .start(
                { facingMode: 'environment' },
                config,
                (decodedText) => {
                    // Stop scanning after first successful read
                    scanner.stop().then(() => {
                        setScanning(false);
                        onScan(decodedText);
                    }).catch(() => { });
                },
                () => { } // ignore scan failures (no barcode in frame)
            )
            .then(() => {
                setScanning(true);
                setError(null);
            })
            .catch((err) => {
                console.error('Camera start failed:', err);
                setError(
                    err.toString().includes('NotAllowed')
                        ? 'Camera access denied. Please allow camera permissions.'
                        : 'Could not start camera. Make sure you\'re on HTTPS.'
                );
            });

        return () => {
            if (html5QrRef.current?.isScanning) {
                html5QrRef.current.stop().catch(() => { });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = () => {
        if (html5QrRef.current?.isScanning) {
            html5QrRef.current.stop().catch(() => { });
        }
        onClose();
    };

    return (
        <div className="scanner-overlay">
            <div className="scanner-container">
                <div className="scanner-header">
                    <span className="scanner-title">📷 Scan Barcode</span>
                    <button className="scanner-close" onClick={handleClose}>✕</button>
                </div>
                <div id="barcode-reader" ref={scannerRef} className="scanner-viewport" />
                {error && (
                    <div className="scanner-error">{error}</div>
                )}
                {scanning && (
                    <div className="scanner-hint">Point camera at a barcode</div>
                )}
            </div>
        </div>
    );
}
